import type Luminary from '@lumen5/luminary';

import type { Conversation } from '../Conversation';

import { buildHtmlDesignTurnContext } from './htmlDesignContext';

import { markScenesBusy } from 'js/src/data/agent-activity';
import { clearHtmlDesignTarget, setHtmlDesignTarget } from 'js/src/data/html-design';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

export interface DesignJobResult {
    success: boolean;
    summary: string;
}

/**
 * Run one design generation end-to-end. The conversation is caller-created so that a ConversationTool
 * caller keeps nested rendering and usage attribution under its own tool call, and binding it to a
 * (sceneId, assetId) target is how the design tools resolve what they act on.
 */
export async function runDesignJob(config: {
    conversation: Conversation;
    luminary: Luminary;
    dispatch: AppDispatch;
    getState: GetRootState;
    sceneId: string;
    assetId: string;
    brief: string;
    parentActionGroup: string;
}): Promise<DesignJobResult> {
    const {
        conversation,
        luminary,
        dispatch,
        getState,
        sceneId,
        assetId,
        brief,
        parentActionGroup,
    } = config;
    const conversationId = conversation.getId();

    dispatch(setHtmlDesignTarget({ conversationId, sceneId, assetId }));
    markScenesBusy(dispatch, [sceneId]);
    try {
        const context = buildHtmlDesignTurnContext(luminary, sceneId, assetId, getState());
        if (context) {
            conversation.addSystemMessage(context, true);
        }
        await conversation.addUserMessage(brief);
        const result = await conversation.executeWithLLM({ parentActionGroup });
        if (!result.success) {
            return { success: false, summary: result.error || 'Design generation failed.' };
        }

        const scene = luminary.getSceneById(sceneId);
        const asset = scene?.getAssetById(assetId) as HtmlAsset | undefined;
        if (!asset?.html) {
            return {
                success: false,
                summary: 'The design agent finished without writing a composition.',
            };
        }

        return { success: true, summary: result.message || 'Design generated.' };
    } finally {
        dispatch(clearHtmlDesignTarget(conversationId));
    }
}
